import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
} from "@tanstack/react-query";
import { toast as toastFn } from "@/shared/hooks/use-toast";
import { ApiError } from "@/shared/api/httpClient";
import { errorToast as describeError } from "@/shared/lib/apiErrors";

export type ToastInput = Parameters<typeof toastFn>[0];

type SuccessToastOrFn<TData, TVars> =
  | ToastInput
  | string
  | ((data: TData, vars: TVars) => ToastInput | string | null | undefined | false);

type ErrorToastOrFn<TVars> =
  | false
  | ToastInput
  | string
  | ((err: Error, vars: TVars) => ToastInput | string | null | undefined | false);

export type MutationWithToastOptions<TData, TVars> = {
  mutationFn: (vars: TVars) => Promise<TData>;
  /** Toast shown on success. String → treated as title. Omit → no toast. */
  successToast?: SuccessToastOrFn<TData, TVars>;
  /** Toast shown on error. String → title (description = 公共错误层人话,variant destructive).
   *  `false` → no toast. Omit → 公共错误层按码给标题(未知码「操作没有成功」). */
  errorToast?: ErrorToastOrFn<TVars>;
  /** Query keys to invalidate on success. */
  invalidateKeys?: QueryKey[];
  /** Extra side-effect on success (dialog close, form reset, …). Runs after toast + invalidate. */
  onSuccess?: (data: TData, vars: TVars) => void;
  /** Extra side-effect on error. Runs after toast. */
  onError?: (err: Error, vars: TVars) => void;
};

function resolveSuccessToast<TData, TVars>(
  input: SuccessToastOrFn<TData, TVars> | undefined,
  data: TData,
  vars: TVars
): ToastInput | null {
  if (input === undefined) return null;
  const resolved = typeof input === "function" ? input(data, vars) : input;
  if (!resolved) return null;
  if (typeof resolved === "string") return { title: resolved };
  return resolved;
}

function resolveErrorToast<TVars>(
  input: ErrorToastOrFn<TVars> | undefined,
  err: Error,
  vars: TVars
): ToastInput | null {
  if (input === false) return null;
  // 0926 修复波 E:默认 / 字符串题两支的描述都走公共错误层(积分不足带「联系管理员充值」、
  // 断网/超时/网关给人话、未知码的码与排查号落描述末行);默认标题按码给人话。
  if (input === undefined) return describeError(err);
  const resolved = typeof input === "function" ? input(err, vars) : input;
  if (!resolved) return null;
  if (typeof resolved === "string") {
    // 调用方的语境标题保留(「共享失败(未生效,可重试)」这类比码表更贴场景)
    return { title: resolved, description: describeError(err).description, variant: "destructive" };
  }
  return resolved;
}

/**
 * 失败 toast 统一带排查号(row-111,0811 学良:「保存失败」无排查号,报障只有一句话术,
 * 后端日志无从查起)。httpClient 每个请求都发 X-Trace-ID 且后端逐行落日志 —— 把它亮给
 * 用户抄,一个号直达那次请求。只动 string description(ReactNode 语义不明不碰),
 * 只在 ApiError 且真有 traceId 时追加。
 */
function withTraceId(t: ToastInput, err: Error): ToastInput {
  if (!(err instanceof ApiError) || !err.traceId) return t;
  const desc = t.description;
  if (desc !== undefined && typeof desc !== "string") return t;
  if (typeof desc === "string" && desc.includes(err.traceId)) return t;
  return { ...t, description: `${desc ? desc + "\n" : ""}排查号 ${err.traceId}` };
}

export function useMutationWithToast<TData = unknown, TVars = void>(
  opts: MutationWithToastOptions<TData, TVars>
): UseMutationResult<TData, Error, TVars> {
  const queryClient = useQueryClient();

  return useMutation<TData, Error, TVars>({
    mutationFn: opts.mutationFn,
    onSuccess: (data, vars) => {
      const t = resolveSuccessToast(opts.successToast, data, vars);
      if (t) toastFn(t);
      if (opts.invalidateKeys) {
        opts.invalidateKeys.forEach((k) => queryClient.invalidateQueries({ queryKey: k }));
      }
      opts.onSuccess?.(data, vars);
    },
    onError: (err, vars) => {
      const t = resolveErrorToast(opts.errorToast, err, vars);
      if (t) toastFn(withTraceId(t, err));
      opts.onError?.(err, vars);
    },
  });
}
